// ** Modules
import { useId } from 'react';

// ** UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';

// ** Styles
import { DIN } from '@/styles/globalStyles';

type DeleteConfirmDialogProps = {
	open: boolean;
	count: number;
	onClose: () => void;
	onConfirm: () => void;
};

const DeleteConfirmDialog = ({ open, count, onClose, onConfirm }: DeleteConfirmDialogProps) => {
	const titleId = useId();
	const descId = useId();

	return (
		<Dialog
			open={open}
			onClose={onClose}
			role="alertdialog"
			aria-labelledby={titleId}
			aria-describedby={descId}
			slotProps={{
				paper: {
					sx: {
						width: { xs: '100%', sm: 480 },
						maxWidth: '100%',
						borderRadius: '8px',
						bgcolor: 'common.white',
						p: { xs: 6, sm: 8 },
						m: { xs: 4, sm: 8 },
						boxSizing: 'border-box',
						position: 'relative'
					}
				},
				backdrop: {
					sx: { bgcolor: 'rgba(0,0,0,0.55)' }
				}
			}}
			maxWidth={false}
		>
			<IconButton
				onClick={onClose}
				aria-label="Schließen"
				sx={{
					position: 'absolute',
					top: { xs: 12, sm: 16 },
					right: { xs: 12, sm: 16 },
					color: 'text.primary'
				}}
			>
				<CloseIcon fontSize="small" />
			</IconButton>

			<Typography id={titleId} variant="title-sm" component="h2" sx={{ color: 'common.black', mb: 4, pr: 8 }}>
				Einträge löschen
			</Typography>

			<Typography id={descId} variant="body-md" component="p" sx={{ color: 'text.secondary', mb: 8 }}>
				{count === 1
					? 'Möchten Sie diesen Mitarbeiter-Zugang wirklich löschen? Dies hat Auswirkungen und kann nicht rückgängig gemacht werden.'
					: `Möchten Sie diese ${count} Mitarbeiter-Zugänge wirklich löschen? Dies hat Auswirkungen und kann nicht rückgängig gemacht werden.`}
			</Typography>

			<Box sx={{ display: 'flex', gap: 4 }}>
				<Button
					variant="contained"
					onClick={onConfirm}
					disableElevation
					sx={{
						bgcolor: 'error.main',
						color: 'common.white',
						fontFamily: DIN,
						fontWeight: 500,
						fontSize: '14px',
						textTransform: 'none',
						borderRadius: '4px',
						px: 6,
						py: 2,
						'&:hover': { bgcolor: 'error.dark' }
					}}
				>
					Löschen
				</Button>
				<Button
					variant="outlined"
					color="secondary"
					onClick={onClose}
					disableElevation
					sx={(theme) => ({
						borderColor: 'divider',
						color: 'text.primary',
						fontFamily: DIN,
						fontWeight: 500,
						fontSize: '14px',
						textTransform: 'none',
						borderRadius: '4px',
						px: 6,
						py: 2,
						transition: 'border-color 160ms ease, background-color 160ms ease, color 160ms ease',
						'&:hover': {
							borderColor: 'text.secondary',
							backgroundColor: alpha(theme.palette.common.black, 0.06),
							color: 'text.primary'
						}
					})}
				>
					Abbrechen
				</Button>
			</Box>
		</Dialog>
	);
};

export default DeleteConfirmDialog;
